import dayjs, { Dayjs } from "dayjs"
import { getWeekdayAbbrev } from "./dateTimeUtils"

export type MyPlacesChartDatum = {
  dateKey: string
  dayLabel: string
  safePlaces: number
  riskyPlaces: number
}

export type MyPlacesTrendVisit = {
  created_at: string
  relapse_prevention_place: {
    category: string
    place_type: string
  }
}

/**
 * Creates fourteen local calendar-day buckets ending on the supplied date and
 * counts safe and risky geofence-entry visits in their matching day.
 */
export function buildMyPlacesTrendData(
  visits: readonly MyPlacesTrendVisit[],
  referenceDate: Dayjs = dayjs()
): MyPlacesChartDatum[] {
  const chartData = Array.from({ length: 14 }, (_, index) => {
    const day = referenceDate.subtract(13 - index, "day")

    return {
      dateKey: day.format("YYYY-MM-DD"),
      dayLabel: `${day.date()} ${getWeekdayAbbrev(day)}`,
      safePlaces: 0,
      riskyPlaces: 0,
    }
  })
  const dataByDay = new Map(chartData.map((datum) => [datum.dateKey, datum]))

  visits.forEach((visit) => {
    const datum = dataByDay.get(dayjs(visit.created_at).format("YYYY-MM-DD"))

    if (!datum) {
      return
    }

    if (
      visit.relapse_prevention_place.place_type === "safe" &&
      visit.relapse_prevention_place.category !== "Home"
    ) {
      datum.safePlaces += 1
    }

    if (visit.relapse_prevention_place.place_type === "risky") {
      datum.riskyPlaces += 1
    }
  })

  return chartData
}

/** Returns whether any safe or risky visit falls inside the chart window. */
export function hasMyPlacesTrendData(data: readonly MyPlacesChartDatum[]) {
  return data.some((datum) => datum.safePlaces > 0 || datum.riskyPlaces > 0)
}

/** Formats the inclusive range represented by a My Places chart. */
export function getMyPlacesTrendCaption(data: readonly MyPlacesChartDatum[]) {
  if (data.length === 0) {
    return ""
  }

  const start = dayjs(data[0].dateKey).format("MMM D")
  const end = dayjs(data[data.length - 1].dateKey).format("MMM D")

  return `${start}–${end}`.toUpperCase()
}
